import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { AgeCategory, ReverseCalculationInput, ReverseCalculationResult, AdvancedTaxOptions, CalculatorMode, ForwardCalculationResult } from '../../types/tax';
import { calculateReverseIncome, calculateTax } from '../../utils/calculations';
import { validateNetIncome } from '../../utils/validation';
import { useScenarioContext } from '../../hooks/useScenariosContext';
import { decodeCalculationURL } from '../../utils/export/urlShareGenerator';
import { measureCalculationPerformance } from '../../utils/performance/webVitals';
import { TaxDataManager } from '../../utils/taxYear/TaxDataManager';
import { trackCalcRun, trackCalcOptionsUsed } from '../../services/analyticsService';
import IncomeInputForm from './IncomeInputForm';
import ResultsDisplay from './ResultsDisplay';
import ForwardResultsDisplay from './ForwardResultsDisplay';
import ModeToggle from './ModeToggle';
import ResultsToggle from './ResultsToggle';
import type { FormData } from './CalculationFormData';

// fx-1.2: emit calc_run + calc_options_used on each completed calculation.
// Only categorical/boolean signals are sent — no PII, no income or amounts.
const trackCalculationEvents = (data: FormData, mode: 'reverse' | 'forward'): void => {
  const taxYear = TaxDataManager.getInstance().getCurrentTaxYear();
  trackCalcRun({ taxYear, calcMode: mode });
  trackCalcOptionsUsed({
    ageBand: data.ageCategory || 'unknown',
    incomeFrequency: data.incomeFrequency,
    hasMedicalAid: !!data.hasMedicalAid,
    hasPension: !!data.hasPension,
    hasTravelAllowance: !!data.hasTravelAllowance,
    calculationType: data.showAdvancedOptions ? 'advanced' : 'basic',
    medicalDependents: parseInt(data.medicalAidDependents) || 0,
  });
};

const DEFAULT_FORM_VALUES: FormData = {
  income: '',
  ageCategory: '',
  incomeFrequency: 'monthly',
  showAdvancedOptions: false,
  hasMedicalAid: false,
  medicalAidMainMember: false,
  medicalAidDependents: '0',
  hasPension: false,
  pensionType: 'percentage',
  pensionAmount: '',
  hasTravelAllowance: false,
  vehicleValue: '',
  privateUsePercentage: '0',
  travelAllowanceAmount: '',
};

interface ReverseCalculatorProps {
  calculationResult: ReverseCalculationResult | null;
  setCalculationResult: (result: ReverseCalculationResult | null) => void;
  lastInput: ReverseCalculationInput | null;
  setLastInput: (input: ReverseCalculationInput | null) => void;
}

export default function ReverseCalculator({
  calculationResult,
  setCalculationResult,
  lastInput,
  setLastInput
}: ReverseCalculatorProps) {
  const [calculatorMode, setCalculatorMode] = useState<CalculatorMode>('reverse');
  const [resultsViewMode, setResultsViewMode] = useState<CalculatorMode>('reverse');
  const [showSaveModal, setShowSaveModal] = useState(false);
  
  // Forward calculator - completely independent state
  const forwardForm = useForm<FormData>({ defaultValues: DEFAULT_FORM_VALUES, mode: 'onChange' });
  const [forwardResult, setForwardResult] = useState<ForwardCalculationResult | null>(null);
  const [forwardError, setForwardError] = useState<string | null>(null);
  const [forwardIsCalculating, setForwardIsCalculating] = useState(false);
  
  // Reverse calculator - completely independent state
  const reverseForm = useForm<FormData>({ defaultValues: DEFAULT_FORM_VALUES, mode: 'onChange' });
  const [reverseError, setReverseError] = useState<string | null>(null);
  const [reverseIsCalculating, setReverseIsCalculating] = useState(false);
  
  const { saveScenario, scenarios } = useScenarioContext();

  // Load shared calculation from URL on component mount
  useEffect(() => {
    const loadSharedCalculation = async () => {
      try {
        const urlData = decodeCalculationURL(window.location.href);
        if (urlData) {
          reverseForm.setValue('income', (urlData.frequency === 'monthly' ? urlData.netIncome / 12 : urlData.netIncome).toString());
          reverseForm.setValue('ageCategory', urlData.ageCategory);
          reverseForm.setValue('incomeFrequency', urlData.frequency);
          
          if (urlData.advancedOptions) {
            reverseForm.setValue('showAdvancedOptions', true);
            
            if (urlData.advancedOptions.medicalAid) {
              reverseForm.setValue('hasMedicalAid', true);
              reverseForm.setValue('medicalAidMainMember', urlData.advancedOptions.medicalAid.mainMember);
              reverseForm.setValue('medicalAidDependents', urlData.advancedOptions.medicalAid.dependents.toString());
            }
            
            if (urlData.advancedOptions.pension) {
              reverseForm.setValue('hasPension', true);
              reverseForm.setValue('pensionType', urlData.advancedOptions.pension.contributionType);
              reverseForm.setValue('pensionAmount', urlData.advancedOptions.pension.contributionAmount.toString());
            }
            
            if (urlData.advancedOptions.travelAllowance) {
              reverseForm.setValue('hasTravelAllowance', true);
              reverseForm.setValue('vehicleValue', urlData.advancedOptions.travelAllowance.vehicleValue.toString());
              reverseForm.setValue('privateUsePercentage', urlData.advancedOptions.travelAllowance.privateUsePercentage.toString());
              reverseForm.setValue('travelAllowanceAmount', urlData.advancedOptions.travelAllowance.travelAllowanceAmount.toString());
            }
          }

          const input: ReverseCalculationInput = {
            desiredNetIncome: urlData.netIncome,
            ageCategory: urlData.ageCategory,
            advancedOptions: urlData.advancedOptions,
          };

          const result = measureCalculationPerformance(
            'URL Shared Calculation',
            () => calculateReverseIncome(input)
          );
          setCalculationResult(result);
          setLastInput(input);
        }
      } catch (error) {
        console.error('Failed to load shared calculation:', error);
      }
    };

    loadSharedCalculation();
  }, [setCalculationResult, setLastInput, reverseForm]);

  // Build advanced options from form data
  const buildAdvancedOptions = (data: FormData): AdvancedTaxOptions | undefined => {
    const advancedOptions: AdvancedTaxOptions = {};
    
    if (data.hasMedicalAid) {
      advancedOptions.medicalAid = {
        hasMedicalAid: true,
        mainMember: data.medicalAidMainMember,
        dependents: parseInt(data.medicalAidDependents) || 0,
      };
    }
    
    if (data.hasPension && data.pensionAmount) {
      const pensionAmount = parseFloat(data.pensionAmount.replace(/[^\d.]/g, '')) || 0;
      if (pensionAmount > 0) {
        const annualPensionAmount = data.pensionType === 'fixed' && data.incomeFrequency === 'monthly'
          ? pensionAmount * 12
          : pensionAmount;
        advancedOptions.pension = {
          hasPension: true,
          contributionType: data.pensionType,
          contributionAmount: annualPensionAmount,
        };
      }
    }
    
    if (data.hasTravelAllowance && (data.vehicleValue || data.travelAllowanceAmount)) {
      advancedOptions.travelAllowance = {
        hasTravelAllowance: true,
        vehicleValue: parseFloat(data.vehicleValue) || 0,
        privateUsePercentage: parseFloat(data.privateUsePercentage) || 0,
        travelAllowanceAmount: parseFloat(data.travelAllowanceAmount) || 0,
      };
    }
    
    return Object.keys(advancedOptions).length > 0 ? advancedOptions : undefined;
  };

  // Reverse calculator handlers
  const onSubmitReverse = async (data: FormData) => {
    try {
      setReverseIsCalculating(true);
      setReverseError(null);

      const netIncomeAmount = parseFloat(data.income.replace(/[^\d.]/g, ''));
      const annualNetIncome = data.incomeFrequency === 'monthly' 
        ? netIncomeAmount * 12 
        : netIncomeAmount;
      
      const validationResult = validateNetIncome(annualNetIncome);
      if (!validationResult.isValid) {
        setReverseError(validationResult.message || 'Validation error');
        return;
      }

      const input: ReverseCalculationInput = {
        desiredNetIncome: annualNetIncome,
        ageCategory: data.ageCategory as AgeCategory,
        advancedOptions: buildAdvancedOptions(data),
      };

      const result = measureCalculationPerformance(
        'Reverse Tax Calculation',
        () => calculateReverseIncome(input)
      );
      setCalculationResult(result);
      setLastInput(input);
      setResultsViewMode('reverse');

      trackCalculationEvents(data, 'reverse');

    } catch (err) {
      setReverseError(err instanceof Error ? err.message : 'An unexpected error occurred');
      console.error('Calculation error:', err);
    } finally {
      setReverseIsCalculating(false);
    }
  };

  const handleResetReverse = () => {
    setCalculationResult(null);
    setLastInput(null);
    setReverseError(null);
    reverseForm.reset(DEFAULT_FORM_VALUES);
  };

  // Forward calculator handlers
  const onSubmitForward = async (data: FormData) => {
    try {
      setForwardIsCalculating(true);
      setForwardError(null);

      const incomeAmount = parseFloat(data.income.replace(/[^\d.]/g, ''));
      const annualGrossIncome = data.incomeFrequency === 'monthly'
        ? incomeAmount * 12
        : incomeAmount;

      if (isNaN(incomeAmount) || incomeAmount <= 0) {
        setForwardError('Please enter a valid income amount');
        return;
      }

      const input = {
        grossIncome: annualGrossIncome,
        ageCategory: data.ageCategory as AgeCategory,
        advancedOptions: buildAdvancedOptions(data),
      };

      const taxResult = measureCalculationPerformance(
        'Forward Tax Calculation',
        () => calculateTax(input)
      );

      const effectiveTaxRate = taxResult.grossIncome > 0
        ? (taxResult.totalDeductions / taxResult.grossIncome) * 100
        : 0;

      const marginalTaxRate = taxResult.taxableIncome > 0
        ? (taxResult.taxAfterRebatesAndCredits / taxResult.taxableIncome) * 100
        : 0;

      const result: ForwardCalculationResult = {
        ...taxResult,
        effectiveTaxRate,
        marginalTaxRate,
      };

      setForwardResult(result);
      setResultsViewMode('forward');

      trackCalculationEvents(data, 'forward');

    } catch (err) {
      setForwardError(err instanceof Error ? err.message : 'An unexpected error occurred');
      console.error('Calculation error:', err);
    } finally {
      setForwardIsCalculating(false);
    }
  };

  const handleResetForward = () => {
    setForwardResult(null);
    setForwardError(null);
    forwardForm.reset(DEFAULT_FORM_VALUES);
  };

  const handleModeChange = (mode: CalculatorMode) => {
    setCalculatorMode(mode);
  };

  const handleSaveScenario = () => {
    setShowSaveModal(true);
  };

  const handleSaveScenarioComplete = (name: string) => {
    if (calculationResult && lastInput) {
      saveScenario(
        name,
        lastInput,
        calculationResult,
        reverseForm.watch('incomeFrequency')
      );
      setShowSaveModal(false);
    }
  };

  // Check if both results exist for showing the toggle
  // ResultsToggle component handles this internally

  // Render the appropriate form based on calculator mode
  const renderForm = () => {
    if (calculatorMode === 'forward') {
      return (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">
              Forward Tax Calculator
            </h2>
            <p className="text-gray-600 dark:text-slate-400">
              Enter your gross salary to calculate your net (take-home) income after tax deductions.
            </p>
          </div>

          <IncomeInputForm
            register={forwardForm.register}
            errors={forwardForm.formState.errors}
            onSubmit={forwardForm.handleSubmit(onSubmitForward)}
            isCalculating={forwardIsCalculating}
            watchedValues={forwardForm.watch()}
            onReset={handleResetForward}
            setValue={forwardForm.setValue}
            mode="forward"
          />

          {forwardError && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Error</h3>
                  <p className="text-sm text-red-700 dark:text-red-400 mt-1">{forwardError}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            Reverse Tax Calculator
          </h2>
          <p className="text-gray-600 dark:text-slate-400">
            Enter your desired net (take-home) income to calculate the required gross salary.
          </p>
        </div>

        <IncomeInputForm
          register={reverseForm.register}
          errors={reverseForm.formState.errors}
          onSubmit={reverseForm.handleSubmit(onSubmitReverse)}
          isCalculating={reverseIsCalculating}
          watchedValues={reverseForm.watch()}
          onReset={handleResetReverse}
          setValue={reverseForm.setValue}
          mode="reverse"
        />

        {reverseError && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Error</h3>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">{reverseError}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render results section - shared between both modes
  const renderResults = () => {
    const hasAnyResult = !!calculationResult || !!forwardResult;
    if (!hasAnyResult) return null;

    return (
      <>
        {/* Results Toggle - only shown when both results exist */}
        <ResultsToggle
          mode={resultsViewMode}
          onModeChange={setResultsViewMode}
          hasReverseResult={!!calculationResult}
          hasForwardResult={!!forwardResult}
        />

        {/* Results Display */}
        {resultsViewMode === 'reverse' && calculationResult && lastInput && (
          <ResultsDisplay 
            result={calculationResult}
            onRecalculate={handleResetReverse}
            inputFrequency={reverseForm.watch('incomeFrequency')}
            onSaveScenario={handleSaveScenario}
            calculationInput={lastInput}
            scenarios={scenarios}
          />
        )}

        {resultsViewMode === 'forward' && forwardResult && (
          <ForwardResultsDisplay
            result={forwardResult}
            onRecalculate={handleResetForward}
            inputFrequency={forwardForm.watch('incomeFrequency')}
          />
        )}
      </>
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <ModeToggle mode={calculatorMode} onModeChange={handleModeChange} />
      
      {renderForm()}
      
      {renderResults()}
      
      {/* Save Scenario Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4">Save Scenario</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const name = formData.get('scenarioName') as string;
              if (name?.trim()) {
                handleSaveScenarioComplete(name.trim());
              }
            }} className="space-y-4">
              <div>
                <label htmlFor="scenarioName" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Scenario Name
                </label>
                <input
                  type="text"
                  id="scenarioName"
                  name="scenarioName"
                  placeholder="e.g., Current Job, Job Offer A, Dream Job"
                  className="input-field w-full"
                  autoFocus
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowSaveModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  Save Scenario
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
